import Dispatcher from '../dispatcher/CentricDispatcher.react';
import { ActionTypes, StatTypes, SearchTypes } from '../constants/CentricConstants';

import ModuleApi from '../server/ModuleApi';

function createSuccessHandler() {
    const actions = Array.from(arguments);
    return function(response) {
        actions.forEach(function(action) {
            Dispatcher.dispatch({
                type: action,
                data: response.data
            });
        });
    };
}

function createErrorHandler() {
    const actions = Array.from(arguments);
    return function(response) {
        actions.forEach(function(action) {
            Dispatcher.dispatch({
                type: action,
                data: response.message
            });
        });
    };
}

const _handleFormError = createErrorHandler(ActionTypes.FORM_SUBMIT_ERROR);
const _handleException = createErrorHandler(ActionTypes.API_EXCEPTION);

export default {

    getAll: (params, clearAll = true) => {
        const types = {
            success:    ActionTypes.MODULE_LIST,
            failure:    ActionTypes.MODULE_LIST_ERROR
        };

        return Dispatcher.dispatchAsync(ModuleApi.getAll(params), types, {params, clearAll});
    },

    getMy: (type, params) => {
        const handleSuccess = createSuccessHandler(ActionTypes.MODULE_LIST);
        const handleError = createErrorHandler(ActionTypes.MODULE_LIST_ERROR);

        return ModuleApi.getMy(type, params)
            .then(handleSuccess, handleError)
            .catch(_handleException);
    },
    getMyStats: (completed) => {
        const types = {
            request:    ActionTypes.STATS_INFO_REQUEST,
            success:    ActionTypes.STATS_INFO_SUCCESS,
            failure:    ActionTypes.STATS_INFO_FAILURE
        };
        const key = ( completed === undefined
            ? StatTypes.MY_MODULES
            : (!!completed ? StatTypes.MY_MODULES_COMPLETED : StatTypes.MY_MODULES_NOT_COMPLETED));
        return Dispatcher.dispatchAsync(ModuleApi.getMyStats(completed), types, {completed, key});
    },
    getById: (id) => {
        const handleSuccess = createSuccessHandler(ActionTypes.MODULE_INFO);
        const handleError = createErrorHandler(ActionTypes.MODULE_INFO_ERROR);

        return ModuleApi.getById(id)
            .then(handleSuccess, handleError)
            .catch(_handleException);
    },
    getByCategory: (categoryId) => {
        const handleSuccess = createSuccessHandler(ActionTypes.MODULE_LIST);
        const handleError = createErrorHandler(ActionTypes.MODULE_LIST_ERROR);

        return ModuleApi.getByCategory(categoryId)
            .then(handleSuccess, handleError)
            .catch(_handleException);
    },
    getByEvent: (eventId) => {
        const types = {
            success:    ActionTypes.MODULE_INFO,
            failure:    ActionTypes.MODULE_INFO_ERROR
        };

        return Dispatcher.dispatchAsync(ModuleApi.getByEvent(eventId), types, {eventId});
    },
    getSimplifiedList: (moduleIds) => {
        const types = {
            success:    ActionTypes.MODULE_LIST,
            failure:    ActionTypes.MODULE_LIST_ERROR
        };

        return Dispatcher.dispatchAsync(ModuleApi.getSimplifiedList(moduleIds), types, {moduleIds});
    },

    save: function(entity, files) {
        const handleSuccess = createSuccessHandler(ActionTypes.MODULE_ADD, ActionTypes.FORM_SUBMIT_SUCCESS);
        const handleError = createErrorHandler(ActionTypes.MODULE_INFO_ERROR);

        return ModuleApi.save(entity, files)
            .then(handleSuccess, _handleFormError)
            .catch(_handleException);
    },
    update: function(id, entity, files) {
        const handleSuccess = createSuccessHandler(ActionTypes.MODULE_UPDATE, ActionTypes.FORM_SUBMIT_SUCCESS);
        const handleError = createErrorHandler(ActionTypes.MODULE_UPDATE_ERROR);

        return ModuleApi.update(id, entity, files)
            .then(handleSuccess, _handleFormError)
            .catch(_handleException);
    },

    setCode: (id, code) => {
        const types = {
            success: ActionTypes.MODULE_UPDATE,
            failure: [ActionTypes.MODULE_UPDATE_ERROR, ActionTypes.FORM_SUBMIT_ERROR]
        };
        return Dispatcher.dispatchAsync(ModuleApi.setCode(id, code), types, {id, code});
    },

    delete: function(id) {
        const handleSuccess = createSuccessHandler(ActionTypes.MODULE_DELETE);
        const handleError = createErrorHandler(ActionTypes.MODULE_DELETE_ERROR);

        return ModuleApi.delete(id)
            .then(handleSuccess, handleError)
            .catch(_handleException);
    },
    search: function(searchText, params, key = SearchTypes.MODULES) {
        const types = {
            request:    ActionTypes.SEARCH_REQUEST,
            success:    [ActionTypes.SEARCH_SUCCESS, ActionTypes.MODULE_LIST],
            failure:    ActionTypes.SEARCH_FAILURE
        };

        return Dispatcher.dispatchAsync(ModuleApi.search(searchText, params), types, {searchText, params, key});
    },

    searchMy: function(searchText, params) {
        const types = {
            request:    ActionTypes.SEARCH_REQUEST,
            success:    [ActionTypes.SEARCH_SUCCESS, ActionTypes.MODULE_LIST],
            failure:    ActionTypes.SEARCH_FAILURE
        };

        return Dispatcher.dispatchAsync(ModuleApi.searchMy(searchText, params), types, {searchText, params});
    },

    searchMedia: (searchText, params, key = SearchTypes.RESOURCES) => {
        const types = {
            request:    ActionTypes.SEARCH_REQUEST,
            success:    ActionTypes.SEARCH_SUCCESS,
            failure:    ActionTypes.SEARCH_FAILURE
        };

        return Dispatcher.dispatchAsync(ModuleApi.searchMedia(searchText, params), types, {searchText, params, key});
    },

    searchMediaContent: (searchText, params, key = SearchTypes.RESOURCES_CONTENT) => {
        const types = {
            request:    ActionTypes.SEARCH_REQUEST,
            success:    ActionTypes.SEARCH_SUCCESS,
            failure:    ActionTypes.SEARCH_FAILURE
        };

        return Dispatcher.dispatchAsync(ModuleApi.searchMediaContent(searchText, params), types, {searchText, params, key});
    },

    getSimplifiedStatusList: () => {
        const types = {
            success:    ActionTypes.MODULE_STATUS_OPTION_LIST,
            failure:    ActionTypes.MODULE_INFO_ERROR
        };

        return Dispatcher.dispatchAsync(ModuleApi.getSimplifiedStatusList(), types);
    },

    getTypeOptions: () => {
        const types = {
            request:    ActionTypes.MODULE_TYPE_LIST_REQUEST,
            success:    ActionTypes.MODULE_TYPE_LIST_SUCCESS,
            failure:    ActionTypes.MODULE_TYPE_LIST_FAILURE
        };

        return Dispatcher.dispatchAsync(ModuleApi.getTypeOptions(), types);
    },
    getAssets: function(id) {
        const handleSuccess = createSuccessHandler(ActionTypes.MODULE_ASSET_LIST);
        const handleError = createErrorHandler(ActionTypes.MODULE_ASSET_LIST_ERROR);

        return ModuleApi.getAssets(id)
            .then(handleSuccess, handleError)
            .catch(_handleException);
    },
    addAsset: function(id, assetId) {
        const types = {
            success: ActionTypes.MODULE_ASSET_ADD,
            failure: [ActionTypes.MODULE_ASSET_ADD_ERROR, ActionTypes.FORM_SUBMIT_ERROR]
        };

        return Dispatcher.dispatchAsync(ModuleApi.addAsset(id, assetId), types, {id, assetId});
    },
    removeAsset: function(id, assetId) {
        const types = {
            success: ActionTypes.MODULE_ASSET_DELETE,
            failure: [ActionTypes.MODULE_ASSET_DELETE_ERROR, ActionTypes.FORM_SUBMIT_ERROR]
        };

        return Dispatcher.dispatchAsync(ModuleApi.removeAsset(id, assetId), types, {id, assetId});
    },

    getEvents: (moduleId, params) => {
        const types = {
            request:    ActionTypes.MODULE_EVENT_LIST_REQUEST,
            success:    ActionTypes.MODULE_EVENT_LIST_SUCCESS,
            failure:    ActionTypes.MODULE_EVENT_LIST_FAILURE
        };

        return Dispatcher.dispatchAsync(ModuleApi.getEvents(moduleId, params), types, {moduleId, params});
    },

    addEvent: (moduleId, entity) => {
        const types = {
            request:    ActionTypes.MODULE_EVENT_ADD_REQUEST,
            success:    [ActionTypes.MODULE_EVENT_ADD_SUCCESS, ActionTypes.FORM_SUBMIT_SUCCESS],
            failure:    [ActionTypes.MODULE_EVENT_ADD_FAILURE, ActionTypes.FORM_SUBMIT_ERROR]
        };

        return Dispatcher.dispatchAsync(ModuleApi.addEvent(moduleId, entity), types, {moduleId, entity});
    },

    getEvent: (moduleId, eventId) => {
        const types = {
            request:    ActionTypes.MODULE_EVENT_INFO_REQUEST,
            success:    ActionTypes.MODULE_EVENT_INFO_SUCCESS,
            failure:    ActionTypes.MODULE_EVENT_INFO_FAILURE
        };

        return Dispatcher.dispatchAsync(ModuleApi.getEvent(moduleId, eventId), types, {moduleId, eventId});
    },

    removeEvent: (event) => {
        const types = {
            request:    ActionTypes.MODULE_EVENT_DELETE_REQUEST,
            success:    ActionTypes.MODULE_EVENT_DELETE_SUCCESS,
            failure:    ActionTypes.MODULE_EVENT_DELETE_FAILURE
        };

        return Dispatcher.dispatchAsync(ModuleApi.removeEvent(event.moduleId, event.id), types, {event});
    },

    publishModuleMedia: (moduleId) => {
        const types = {
            request:    ActionTypes.MODULE_MEDIA_PUBLISH_REQUEST,
            success:    ActionTypes.MODULE_MEDIA_PUBLISH_SUCCESS,
            failure:    ActionTypes.MODULE_MEDIA_PUBLISH_FAILURE
        };

        return Dispatcher.dispatchAsync(ModuleApi.publishModuleMedia(moduleId), types, {moduleId});
    },

    getMediaInfo: (id) => {
        const types = {
            request:    ActionTypes.MODULE_MEDIA_INFO_REQUEST,
            success:    ActionTypes.MODULE_MEDIA_INFO_SUCCESS,
            failure:    ActionTypes.MODULE_MEDIA_INFO_FAILURE
        };

        return Dispatcher.dispatchAsync(ModuleApi.getMediaInfo(id), types, {id, suppressErrors: true});
    },

    getSubscriptions: (moduleId, params, clearAll = false) => {
        const types = {
            request:    ActionTypes.MODULE_SUBSCRIPTION_LIST_REQUEST,
            success:    ActionTypes.MODULE_SUBSCRIPTION_LIST_SUCCESS,
            failure:    ActionTypes.MODULE_SUBSCRIPTION_LIST_FAILURE
        };
        return Dispatcher.dispatchAsync(ModuleApi.getSubscriptions(moduleId, params), types, {moduleId, params, clearAll});
    },

    searchSubscriptions: (moduleId, searchText, params) => {
        const types = {
            request:    ActionTypes.SEARCH_REQUEST,
            success:    ActionTypes.SEARCH_SUCCESS,
            failure:    ActionTypes.SEARCH_FAILURE
        };
        return Dispatcher.dispatchAsync(ModuleApi.getSubscriptions(moduleId, {searchText, ...params}), types, {moduleId, searchText, params});
    },

    getUserSubscriptions: (userId) => {
        const types = {
            success:    ActionTypes.MODULE_SUBSCRIPTION_LIST_SUCCESS,
            failure:    ActionTypes.MODULE_SUBSCRIPTION_LIST_FAILURE
        };
        return Dispatcher.dispatchAsync(ModuleApi.getUserSubscriptions(userId), types, {userId});
    },

    getCourseUserSubscriptions: (courseId, userId) => {
        const types = {
            request:    ActionTypes.COURSE_MODULE_SUBSCRIPTION_LIST_REQUEST,
            success:    ActionTypes.COURSE_MODULE_SUBSCRIPTION_LIST_SUCCESS,
            failure:    ActionTypes.COURSE_MODULE_SUBSCRIPTION_LIST_FAILURE
        };
        return Dispatcher.dispatchAsync(ModuleApi.getCourseUserSubscriptions(courseId, userId), types, {courseId, userId});
    },

    deleteSubscription: (moduleId, userId) => {
        const types = {
            request:    ActionTypes.MODULE_SUBSCRIPTION_DELETE_REQUEST,
            success:    ActionTypes.MODULE_SUBSCRIPTION_DELETE_SUCCESS,
            failure:    ActionTypes.MODULE_SUBSCRIPTION_DELETE_FAILURE
        };
        return Dispatcher.dispatchAsync(ModuleApi.deleteSubscription(moduleId, userId), types, {moduleId, userId})
    },

    getSubscription: (moduleId, userId) => {
        const types = {
            request:    ActionTypes.MODULE_SUBSCRIPTION_INFO_REQUEST,
            success:    ActionTypes.MODULE_SUBSCRIPTION_INFO_SUCCESS,
            failure:    ActionTypes.MODULE_SUBSCRIPTION_INFO_FAILURE
        };
        return Dispatcher.dispatchAsync(ModuleApi.getSubscription(moduleId, userId), types, {moduleId, userId, suppressErrors: true});
    },

    saveSubscription: (moduleId, userId, parameters, current) => {
        const types = {
            request:    ActionTypes.MODULE_SUBSCRIPTION_SAVE_REQUEST,
            success:    ActionTypes.MODULE_SUBSCRIPTION_SAVE_SUCCESS,
            failure:    [ActionTypes.MODULE_SUBSCRIPTION_SAVE_FAILURE, ActionTypes.FORM_SUBMIT_ERROR]
        };
        return Dispatcher.dispatchAsync(ModuleApi.saveSubscription(moduleId, userId, parameters), types, {moduleId, userId, parameters, current});
    },

    saveSubscriptionByFilter: (moduleId, filterId, parameters) => {
        const types = {
            request:    ActionTypes.MODULE_SUBSCRIPTION_BY_FILTER_SAVE_REQUEST,
            success:    [ActionTypes.MODULE_SUBSCRIPTION_BY_FILTER_SAVE_SUCCESS, ActionTypes.FORM_SUBMIT_SUCCESS],
            failure:    [ActionTypes.MODULE_SUBSCRIPTION_BY_FILTER_SAVE_FAILURE, ActionTypes.FORM_SUBMIT_ERROR]
        };
        return Dispatcher.dispatchAsync(ModuleApi.saveSubscriptionByFilter(moduleId, filterId, parameters), types);
    },

    assignUsersToEvent: (moduleId, eventId, users) => {
        const types = {
            request:    ActionTypes.MODULE_EVENT_ASSIGN_REQUEST,
            success:    ActionTypes.MODULE_EVENT_ASSIGN_SUCCESS,
            failure:    [ActionTypes.MODULE_EVENT_ASSIGN_FAILURE, ActionTypes.FORM_SUBMIT_ERROR]
        };

        return Dispatcher.dispatchAsync(ModuleApi.assignUsersToEvent(moduleId, eventId, {users}), types, {moduleId, eventId, users});
    },

    createRegistration: (moduleId, userId) => {
        const types = {
            request:    ActionTypes.MODULE_REGISTRATION_SAVE_REQUEST,
            success:    ActionTypes.MODULE_REGISTRATION_SAVE_SUCCESS,
            failure:    ActionTypes.MODULE_REGISTRATION_SAVE_FAILURE
        };
        return Dispatcher.dispatchAsync(ModuleApi.createRegistration(moduleId, userId), types, {moduleId, userId});
    },

    listRegistrationHistory: (moduleId, userId) => {
        const types = {
            request:    ActionTypes.REGISTRATION_LIST_REQUEST,
            success:    ActionTypes.REGISTRATION_LIST_SUCCESS,
            failure:    ActionTypes.REGISTRATION_LIST_FAILURE
        };

        return Dispatcher.dispatchAsync(ModuleApi.listRegistrationHistory(moduleId, userId), types, {moduleId, userId});
    },

    resetSubscription: (moduleId, userId, current) => {
        const types = {
            request:    ActionTypes.MODULE_SUBSCRIPTION_SAVE_REQUEST,
            success:    ActionTypes.MODULE_SUBSCRIPTION_SAVE_SUCCESS,
            failure:    ActionTypes.MODULE_SUBSCRIPTION_SAVE_FAILURE
        };
        return Dispatcher.dispatchAsync(ModuleApi.resetRegistration(moduleId, userId), types, {moduleId, userId, current});
    },

    switchRegistration: (registrationId) => {
        const types = {
            request:    ActionTypes.MODULE_SUBSCRIPTION_SWITCH_REQUEST,
            success:    ActionTypes.MODULE_SUBSCRIPTION_SWITCH_SUCCESS,
            failure:    ActionTypes.MODULE_SUBSCRIPTION_SWITCH_FAILURE
        };

        return Dispatcher.dispatchAsync(ModuleApi.switchRegistration(registrationId), types, {registrationId});
    },

    getMyClasses: (params) => {
        const types = {
            request:    ActionTypes.MODULE_CLASS_LIST_REQUEST,
            success:    ActionTypes.MODULE_CLASS_LIST_SUCCESS,
            failure:    ActionTypes.MODULE_CLASS_LIST_FAILURE
        };

        return Dispatcher.dispatchAsync(ModuleApi.getMyClasses(params), types, params);
    },

    getMyClassesStats: () => {
        const types = {
            request:    ActionTypes.STATS_INFO_REQUEST,
            success:    ActionTypes.STATS_INFO_SUCCESS,
            failure:    ActionTypes.STATS_INFO_FAILURE
        };

        return Dispatcher.dispatchAsync(ModuleApi.getMyClassesStats(), types, { key: StatTypes.MY_CLASSES } );

    },

    getMyResources: () => {
        const types = {
            success:    ActionTypes.MODULE_LIST,
            failure:    ActionTypes.MODULE_LIST_ERROR
        };

        return Dispatcher.dispatchAsync(ModuleApi.getMyResources(), types);
    },

    getMyResourceStats: () => {
        const types = {
            request:    ActionTypes.STATS_INFO_REQUEST,
            success:    ActionTypes.STATS_INFO_SUCCESS,
            failure:    ActionTypes.STATS_INFO_FAILURE
        };

        return Dispatcher.dispatchAsync(ModuleApi.getMyResourceStats(), types, { key: StatTypes.MY_RESOURCE_STATS } );
    },

    getModuleCount: () => {
        const types = {
            request:    ActionTypes.STATS_INFO_REQUEST,
            success:    ActionTypes.STATS_INFO_SUCCESS,
            failure:    ActionTypes.STATS_INFO_FAILURE
        };

        return Dispatcher.dispatchAsync(ModuleApi.getCount(), types, { key: StatTypes.MODULE_COUNT } );

    },
    getResourceCount: () => {
        const types = {
            request:    ActionTypes.STATS_INFO_REQUEST,
            success:    ActionTypes.STATS_INFO_SUCCESS,
            failure:    ActionTypes.STATS_INFO_FAILURE
        };

        return Dispatcher.dispatchAsync(ModuleApi.getCount({type: 'resource'}), types, { key: StatTypes.RESOURCE_COUNT } );
    },

    // Module Trainers:
    getTrainers: (moduleId) => {
        const types = {
//            request:    ActionTypes.MODULE_TRAINER_LIST_REQUEST,
            success:    ActionTypes.MODULE_TRAINER_LIST_SUCCESS,
            failure:    ActionTypes.MODULE_TRAINER_LIST_FAILURE
        };
        return Dispatcher.dispatchAsync(ModuleApi.getTrainers(moduleId), types, {moduleId});
    },
    addTrainer: (moduleId, trainerId) => {
        const types = {
            request:    ActionTypes.MODULE_TRAINER_ADD_REQUEST,
            success:    ActionTypes.MODULE_TRAINER_ADD_SUCCESS,
            failure:    ActionTypes.MODULE_TRAINER_ADD_FAILURE
        };
        return Dispatcher.dispatchAsync(ModuleApi.addTrainer(moduleId, trainerId), types, {moduleId, trainerId});
    },
    removeTrainer: (moduleId, trainerId) => {
        const types = {
            request:    ActionTypes.MODULE_TRAINER_DELETE_REQUEST,
            success:    ActionTypes.MODULE_TRAINER_DELETE_SUCCESS,
            failure:    ActionTypes.MODULE_TRAINER_DELETE_FAILURE
        };
        return Dispatcher.dispatchAsync(ModuleApi.removeTrainer(moduleId, trainerId), types, {moduleId, trainerId});
    },

    getReminders: (moduleId) => {
        const types = {
            request:    ActionTypes.MODULE_REMINDER_LIST_REQUEST,
            success:    ActionTypes.MODULE_REMINDER_LIST_SUCCESS,
            failure:    ActionTypes.MODULE_REMINDER_LIST_FAILURE
        };

        return Dispatcher.dispatchAsync(ModuleApi.getReminders(moduleId), types, {moduleId});
    },

    addReminder: (moduleId, draft) => {
        const types = {
            request:    ActionTypes.MODULE_REMINDER_ADD_REQUEST,
            success:    ActionTypes.MODULE_REMINDER_ADD_SUCCESS,
            failure:    ActionTypes.MODULE_REMINDER_ADD_FAILURE
        };

        return Dispatcher.dispatchAsync(ModuleApi.addReminder(moduleId, draft), types, {moduleId, data: draft});
    },

    updateReminder: (current, draft) => {
        const types = {
            request:    ActionTypes.MODULE_REMINDER_UPDATE_REQUEST,
            success:    ActionTypes.MODULE_REMINDER_UPDATE_SUCCESS,
            failure:    ActionTypes.MODULE_REMINDER_UPDATE_FAILURE
        };

        return Dispatcher.dispatchAsync(ModuleApi.updateReminder(current.moduleId, draft), types, {current, draft});
    },

    removeReminder: (current) => {
        const types = {
            request:    ActionTypes.MODULE_REMINDER_DELETE_REQUEST,
            success:    ActionTypes.MODULE_REMINDER_DELETE_SUCCESS,
            failure:    ActionTypes.MODULE_REMINDER_DELETE_FAILURE
        };

        return Dispatcher.dispatchAsync(ModuleApi.removeReminder(current.moduleId, current.id), types, {current});
    },

    removeAllReminders: (moduleId) => {
        const types = {
            request:    ActionTypes.MODULE_REMINDER_DELETE_ALL_REQUEST,
            success:    ActionTypes.MODULE_REMINDER_DELETE_ALL_SUCCESS,
            failure:    ActionTypes.MODULE_REMINDER_DELETE_ALL_FAILURE
        };

        return Dispatcher.dispatchAsync(ModuleApi.removeAllReminders(moduleId), types, {moduleId});
    },

    getAccessGroups: (moduleId) => {
        const types = {
            request:    ActionTypes.MODULE_ACCESS_GROUP_LIST_REQUEST,
            success:    ActionTypes.MODULE_ACCESS_GROUP_LIST_SUCCESS,
            failure:    ActionTypes.MODULE_ACCESS_GROUP_LIST_FAILURE
        };

        return Dispatcher.dispatchAsync(ModuleApi.getAccessGroups(moduleId), types, {moduleId});
    },

    addAccessGroup: (moduleId, accessGroupId) => {
        const types = {
            request:    ActionTypes.MODULE_ACCESS_GROUP_ADD_REQUEST,
            success:    ActionTypes.MODULE_ACCESS_GROUP_ADD_SUCCESS,
            failure:    [ActionTypes.MODULE_ACCESS_GROUP_ADD_FAILURE, ActionTypes.FORM_SUBMIT_ERROR]
        };

        return Dispatcher.dispatchAsync(ModuleApi.addAccessGroup(moduleId, accessGroupId), types, {moduleId, accessGroupId});
    },

    removeAccessGroup: (moduleId, accessGroupId) => {
        const types = {
            request:    ActionTypes.MODULE_ACCESS_GROUP_REMOVE_REQUEST,
            success:    ActionTypes.MODULE_ACCESS_GROUP_REMOVE_SUCCESS,
            failure:    [ActionTypes.MODULE_ACCESS_GROUP_REMOVE_FAILURE, ActionTypes.FORM_SUBMIT_ERROR]
        };

        return Dispatcher.dispatchAsync(ModuleApi.removeAccessGroup(moduleId, accessGroupId), types, {moduleId, accessGroupId});
    }
};
